import { ref } from 'vue';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import { Point } from 'ol/geom';
import { fromLonLat } from 'ol/proj';
import VectorLayer from 'ol/layer/Vector';
import moment from 'moment/moment';

// for each layer
// how about data?
// init layer
// create items etc get style
// refresh layer
//get style????
// onmounted

const useTreeLayers = ({ map, zoomMinMax }) => {
    const treeLayer = ref(new VectorLayer());
    const allTrees = ref([]);
    const treeSource = ref(new VectorSource());
    const noSpeciesMode = ref(false);
    const features = ref([]);

    const loadInspectionTreeSources = (trees) => {
        //treeSource.value.clear();
        const features = [];
        Object.keys(trees).forEach((key) => {
            const thisFeature = trees[key];
            features[key] = new Feature(
                new Point(fromLonLat([parseFloat(thisFeature.lng), parseFloat(thisFeature.lat)], 'EPSG:3857'))
            );
            features[key].setId(thisFeature.id);
            features[key].set('count', thisFeature.count);
            features[key].set('is_grouped', thisFeature.is_grouped);
            features[key].set('treeId', thisFeature.id);
            features[key].set('speciesId', thisFeature.trees_species_id);
            features[key].set('assetId', thisFeature.asset_id);
            features[key].set('riskId', thisFeature.trees_risk_id);
            features[key].set('updatedAt', thisFeature.updated_at);
            features[key].set('treeStatus', thisFeature.trees_status_id);
            features[key].set('approved', thisFeature.approved);
            features[key].set('proStatus', thisFeature.status);
            features[key].set('privateTree', thisFeature.private_tree);
        });
        treeSource.value.addFeatures(features);
    };

    const loadAllTreeSources = (trees) => {
        treeSource.value.clear();
        const features = [];
        Object.keys(trees).forEach((key) => {
            const thisFeature = trees[key];
            features[key] = new Feature(
                new Point(fromLonLat([parseFloat(thisFeature.lng), parseFloat(thisFeature.lat)], 'EPSG:3857'))
            );
            features[key].setId(thisFeature.id);
            features[key].set('count', thisFeature.count);
            features[key].set('treeId', thisFeature.id);
            features[key].set('is_grouped', thisFeature.is_grouped);
            features[key].set('speciesId', thisFeature.trees_species_id);
            features[key].set('commonName', thisFeature.common_name);
            features[key].set('genusName', thisFeature.genus);
            features[key].set('assetId', thisFeature.asset_id);
            features[key].set('riskId', thisFeature.trees_risk_id);
            features[key].set('updatedAt', thisFeature.updated_at);
            features[key].set('statusId', thisFeature.trees_status_id);
            features[key].set('maintId', null);
            features[key].set('workTasks', null);
            features[key].set('inspNotes', null);
            features[key].set('workComplete', null);
            features[key].set('priority', null);
            features[key].set('pointType', null);
        });
        treeSource.value.addFeatures(features);
    };

    const loadPlantingTreeSources = (trees) => {
        const features = [];
        Object.keys(trees).forEach((key) => {
            const thisFeature = trees[key];
            features[key] = new Feature(
                new Point(fromLonLat([parseFloat(thisFeature.lng), parseFloat(thisFeature.lat)], 'EPSG:3857'))
            );
            features[key].setId(thisFeature.id);
            features[key].set('treeId', thisFeature.id);
            features[key].set('speciesId', thisFeature.species_id);
            features[key].set('commonName', thisFeature.common_name);
            features[key].set('origin', thisFeature.origin);
            features[key].set('genusName', thisFeature.genus);
            features[key].set('speciesName', thisFeature.species);
            features[key].set('species', thisFeature.species);
            features[key].set('variety', thisFeature.variety);
            features[key].set('assetId', thisFeature.asset_id);
            features[key].set('vacantId', thisFeature.vacantId);
            features[key].set('sizeId', thisFeature.size_id);
            features[key].set('pavementId', thisFeature.pavement_id);
            features[key].set('powerlinesId', thisFeature.powerlines_id);
            features[key].set('underground', thisFeature.underground_services);
            features[key].set('notes', thisFeature.notes);
            features[key].set('createdAt', thisFeature.created_at);
            features[key].set('userName', thisFeature.name);
            features[key].set('plantingYear', thisFeature.planting_year);
            features[key].set('approved', thisFeature.approved);
            features[key].set('planted', thisFeature.planted);
            features[key].set('plantedDate', thisFeature.planted_date);
            features[key].set('lng', thisFeature.lng);
            features[key].set('lat', thisFeature.lat);
            features[key].set('potSizeId', thisFeature.pot_size_id);
            features[key].set('potSize', thisFeature.pot_size_name);
            features[key].set('parkTree', thisFeature.park_tree);
            features[key].set('removedTreeId', thisFeature.removed_tree_id);
            features[key].set('development_id', thisFeature.development_id);
            features[key].set('devName', thisFeature.devname);
            features[key].set('development_stage', thisFeature.development_stage);
        });
        treeSource.value.addFeatures(features);
    };

    const loadStumpsTreeSources = (trees) => {
        const features = [];
        Object.keys(trees).forEach((key) => {
            features[key] = new Feature(
                new Point(fromLonLat([parseFloat(trees[key].lng), parseFloat(trees[key].lat)], 'EPSG:3857'))
            );
            features[key].setId(trees[key].id);
            features[key].set('treeId', trees[key].id);
            features[key].set('asset_id', trees[key].asset_id);
            features[key].set('defects', trees[key].defects);
            features[key].set('trees_status_id', trees[key].trees_status_id);
            features[key].set('approx_address', trees[key].approx_address);
            features[key].set('address', trees[key].address);
            features[key].set('stump_size', trees[key].stump_size ? trees[key].stump_size.name : '');
            features[key].set('lat', trees[key].lat);
            features[key].set('lng', trees[key].lng);
            features[key].set('stump_assignments', trees[key].stump_assignments ?? []);
            features[key].set('live_stump_projects', trees[key].live_stump_projects ?? []);
            features[key].set('stump_projects', trees[key].stump_projects ?? []);
            features[key].set('priority', trees[key].stump ? trees[key].stump.priority : 0);
            features[key].set('removed_reason', trees[key].stump ? trees[key].stump.removed_reason : null);
            features[key].set('removed_notes', trees[key] ? trees[key].removed_notes : null);
        });
        treeSource.value.addFeatures(features);
    };

    const loadWateringTreeSources = (trees) => {
        Object.keys(trees).forEach((key) => {
            features.value[key] = new Feature(
                new Point(
                    fromLonLat(
                        [parseFloat(trees[key].lng), parseFloat(trees[key].lat)],
                        'EPSG:3857'
                    )
                )
            );
            features.value[key].setId(trees[key].id);
            features.value[key].set('treeId', trees[key].id);
            features.value[key].set('planted_date', trees[key].planted_date);
            features.value[key].set('last_watered', trees[key].last_watered);
            features.value[key].set('common_name', trees[key].common_name);
            features.value[key].set('genus', trees[key].genus);
            features.value[key].set('water_schedule', trees[key].water_schedule);
            features.value[key].set('amount', trees[key].amount);
            features.value[key].set('final_water', trees[key].final_water);
            if (trees[key].water_schedule) {
                features.value[key].set('next_water', trees[key].water_schedule.next_water);
            } else {
                features.value[key].set('next_water', null);
            }
        });
        treeSource.value.addFeatures(features.value);
    };

    const addTreeSourcesToMapLayer = (getIconRiskStyle) => {
        treeLayer.value = new VectorLayer({
            source: treeSource.value,
            //maxResolution: 0.000006,
            maxResolution: map.value.getView().getResolutionForZoom(zoomMinMax.value),
            style: getIconRiskStyle,
            properties:{
                selectable:true
            }
        });
        map.value.addLayer(treeLayer.value);
        treeLayer.value.setZIndex(50);
    };

    return {
        treeLayer,
        allTrees,
        treeSource,
        noSpeciesMode,
        features,
        loadInspectionTreeSources,
        loadAllTreeSources,
        loadPlantingTreeSources,
        loadStumpsTreeSources,
        loadWateringTreeSources
    };
};

export default useTreeLayers;
